import React from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet'

import Header from '../components/header'
import { headerProps } from '../components/HeaderConfig'
import Footer from '../components/footer'
import ThemeToggle from '../components/theme-toggle'
import useTheme from '../hooks/useTheme'
import useScrollAnimation from '../hooks/useScrollAnimation'
import { articlesData } from '../data/articles'
import './articles.css'

const AnimatedDiv = ({ children, className = '', delay = 0 }) => {
  const [ref, isVisible] = useScrollAnimation({ threshold: 0.05 })
  return (
    <div
      ref={ref}
      className={`art-animated ${isVisible ? 'art-visible' : ''} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  )
}

const Articles = () => {
  const { isDark, toggleTheme } = useTheme()

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  // Estimate reading time from content
  const getReadTime = (content) => {
    if (!content) return '3 min read'
    const words = content.replace(/<[^>]*>/g, '').split(/\s+/).length
    return `${Math.max(3, Math.ceil(words / 200))} min read`
  }

  return (
    <div className="articles-container">
      <Helmet>
        <title>Articles - Abhishek Palit | Architecture, Technology & Sustainability</title>
        <meta property="og:title" content="Articles - Abhishek Palit" />
        <meta
          name="description"
          content="Insights on BIM, AI in construction, sustainability, VDC, and digital transformation in the built environment."
        />
      </Helmet>
      <Header
        {...headerProps}
        rootClassName="headerroot-class-name2"
        themeToggle={<ThemeToggle isDark={isDark} toggleTheme={toggleTheme} />}
      />

      <div className="art-page">
        {/* Hero */}
        <div className="art-hero">
          <span className="art-label">Articles</span>
          <h1 className="art-hero-title">
            Thoughts on technology,<br />
            design & construction.
          </h1>
          <p className="art-hero-subtitle">
            Writing about BIM, AI in construction, sustainability, and the
            digital transformation of the built environment.
          </p>
        </div>

        {/* Featured Article (first one) */}
        {articlesData.length > 0 && (
          <AnimatedDiv className="art-featured-wrapper">
            <Link
              to={`/articles/${articlesData[0].slug}`}
              className="art-featured-link"
            >
              <div className="art-featured">
                {articlesData[0].imageUrl && (
                  <div className="art-featured-image">
                    <img src={articlesData[0].imageUrl} alt={articlesData[0].title} />
                  </div>
                )}
                <div className="art-featured-content">
                  <div className="art-featured-meta">
                    <span className="art-category-badge">{articlesData[0].category}</span>
                    <span className="art-date">{formatDate(articlesData[0].date)}</span>
                    <span className="art-read-time">{getReadTime(articlesData[0].content)}</span>
                  </div>
                  <h2 className="art-featured-title">{articlesData[0].title}</h2>
                  <p className="art-featured-excerpt">{articlesData[0].excerpt}</p>
                  <span className="art-read-link">
                    Read article
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </span>
                </div>
              </div>
            </Link>
          </AnimatedDiv>
        )}

        {/* Article Grid */}
        {articlesData.length > 1 && (
          <div className="art-grid">
            {articlesData.slice(1).map((article, index) => (
              <AnimatedDiv key={article.id} delay={index * 100}>
                <Link
                  to={`/articles/${article.slug}`}
                  className="art-card-link"
                >
                  <div className="art-card">
                    {article.imageUrl && (
                      <div className="art-card-image">
                        <img src={article.imageUrl} alt={article.title} />
                      </div>
                    )}
                    <div className="art-card-content">
                      <div className="art-card-meta">
                        <span className="art-category-badge">{article.category}</span>
                        <span className="art-date">{formatDate(article.date)}</span>
                      </div>
                      <h3 className="art-card-title">{article.title}</h3>
                      <p className="art-card-excerpt">{article.excerpt}</p>
                      <div className="art-card-footer">
                        <span className="art-read-time">{getReadTime(article.content)}</span>
                        <span className="art-read-link">
                          Read
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M5 12h14M12 5l7 7-7 7" />
                          </svg>
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              </AnimatedDiv>
            ))}
          </div>
        )}
      </div>

      <Footer rootClassName="footerroot-class-name4"></Footer>
    </div>
  )
}

export default Articles
